import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { AuthClaims, AuthRoles, AuthUserType } from './auth.types';
import { InfoReportService } from '../services/info-report.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {

  constructor(protected router: Router,
    protected authService: AuthService,
    protected infoReportService: InfoReportService
  ) { }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | boolean {
    if (sessionStorage.getItem(environment.authTokenStorageKey) !== null &&
      Number.parseInt(sessionStorage.getItem(environment.authTokenExpiresStorageKey)) >= Date.now()) {

      let hasAuth: Promise<boolean> | boolean = false;
      
      if (next.data['authRoleUserType']) {
        if (hasAuth = this.hasRequiredRoleUserType(next.data['authRoleUserType'])) {
          return hasAuth;
        } else {
          this.router.navigate(['/home']);
          return false;
        }
      } else if (next.data['authUserType'] && next.data['auth']) {
        if (hasAuth = this.hasRequiredUserType(next.data['authUserType']) && this.hasRequiredRole(next.data['auth'])) {
          return hasAuth;
        } else {
          this.router.navigate(['/home']);
          return false;
        }
      } else if (next.data['auth']) {
        if (hasAuth = this.hasRequiredRole(next.data['auth'])) {
          return hasAuth;
        } else {
          this.router.navigate(['/home']);
          return false;
        }
      }
    }

    this.router.navigate(['/login'], { queryParams: { redirectUrl: state.url } });
    return false;
  }

  protected hasRequiredRoleUserType(roleUserType: { roles: AuthRoles[], userType: AuthUserType }[]): Promise<boolean> | boolean  {
    if (this.authService.userType && this.authService.roles) {
      let hasUserTypeRole = this.authService.hasUserTypeRole(roleUserType);

      if (hasUserTypeRole) {
        return hasUserTypeRole;
      } else {
        return this.authService.hasUserType(null) && this.authService.hasRole(null);
      }
    } else {
      const promise = new Promise<boolean>((resolve, reject) => {
        this.authService.initializeRoles()
          .then(() => {
            let hasUserTypeRole = this.authService.hasUserTypeRole(roleUserType);
            if (hasUserTypeRole) {
              resolve(hasUserTypeRole);
            } else {
              resolve(this.authService.hasUserType(null) && this.authService.hasRole(null));
            }
          }).catch(() => {
            resolve(false);
          });
      });
      return promise;
    }
  }

  protected hasRequiredRole(role: AuthRoles[]): Promise<boolean> | boolean  {
    // If user’s roles already retrieved from the API
    if (this.authService.roles) {
      if (role) {
        return this.authService.hasRole(role);
      } else {
        return this.authService.hasRole(null);
      }
    } else {
      // Otherwise, must request roles from the API first
      const promise = new Promise<boolean>((resolve, reject) => {
        this.authService.initializeRoles()
          .then(() => {
            if (role) {
              resolve(this.authService.hasRole(role));
            } else {
              resolve(this.authService.hasRole(null));
            }

          }).catch(() => {
            resolve(false);
          });
      });
      return promise;
    }
  }

  protected hasRequiredUserType(userType: AuthUserType[]): Promise<boolean> | boolean  {
    // If user's user type already retrieved from API
    if (this.authService.userType) {
      if (userType) {
        return this.authService.hasUserType(userType);
      } else {
        return this.authService.hasUserType(null);
      }
    } else {
      // Otherwise, must request user type from the API first
      const promise = new Promise<boolean>((resolve, reject) => {
        this.authService.initializeRoles()
          .then(() => {
            if (userType) {
              resolve(this.authService.hasUserType(userType));
            } else {
              resolve(this.authService.hasUserType(null));
            }

          }).catch(() => {
            resolve(false);
          });
      });
      return promise;
    }
  }

  protected hasRequiredPermission(claim: AuthClaims[]): Promise<boolean> | boolean  {
    // If user’s claims already retrieved from the API
    if (this.authService.claims) {
      if (claim) {
        return this.authService.hasClaim(claim);
      } else {
        return this.authService.hasClaim(null);
      }
    } else {
      // Otherwise, must request claims from the API first
      const promise = new Promise<boolean>((resolve, reject) => {
        this.authService.initializeClaims()
          .then(() => {
            if (claim) {
              resolve(this.authService.hasClaim(claim));
            } else {
              resolve(this.authService.hasClaim(null));
            }

          }).catch(() => {
            resolve(false);
          });
      });
      return promise;
    }
  }
}
